export const formatAmount = (amount, currencySym = 'Bs', shouldHide = false) => {
    if (shouldHide) return `${currencySym} ****`;
    return `${currencySym} ${Number(amount || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDateShort = (date) => {
    if (!date) return 'Fecha desc.';
    const d = date instanceof Date ? date : (date?.toDate ? date.toDate() : new Date(date));
    return d.toLocaleString('es-BO', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
};

export const formatDate = (date) => {
    if (!date) return '---';
    const d = date instanceof Date ? date : (date?.toDate ? date.toDate() : new Date(date));
    return d.toLocaleString('es-BO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const getSelectionId = (matchId, market, outcome) => {
    if (!market || !outcome) return '';
    const mName = market.name?.toLowerCase().trim() || "";

    // Strict Main Market Check
    const isMain = mName === 'h2h' || mName === '1x2' || mName === 'match winner' || mName === 'money line' || mName === 'ganador del partido';

    let marketIdToUse = isMain ? 'MAIN_MARKET_1X2' : (market.id || 'nomkid');

    // If not main, ALWAYS append name to differentiate markets with same/missing reused IDs
    if (!isMain) {
        marketIdToUse = `${marketIdToUse}_${mName.replace(/[^a-z0-9]/g, '')}`;
    }

    const specifier = market.specifiers ? (typeof market.specifiers === 'object' ? JSON.stringify(market.specifiers) : market.specifiers) : 'main';

    // Outcome part: Combine ID and Name to ensure uniqueness derived from both
    const oId = String(outcome.id || "");
    const oName = (outcome.name || "").toLowerCase().replace(/[^a-z0-9]/g, '');

    // Composite key to guarantee uniqueness even if IDs are reused (e.g. '1', '2') across similar markets
    const outcomeKey = `${oId}_${oName}`;

    return `${matchId}-${marketIdToUse}-${specifier}-${outcomeKey}`;
};

export const getStatusConfig = (status) => {
    switch (status) {
        case 'pending': return { label: 'PENDIENTE', color: 'text-yellow-400', border: 'border-l-4 border-yellow-400', headerBg: 'bg-[#ca8a04]', badgeText: 'text-white' };
        case 'won': return { label: 'GANADA', color: 'text-emerald-400', border: 'border-l-4 border-emerald-500', headerBg: 'bg-[#059669]', badgeText: 'text-white' };
        case 'lost': return { label: 'PERDIDA', color: 'text-red-400', border: 'border-l-4 border-red-500', headerBg: 'bg-[#dc2626]', badgeText: 'text-white' };
        case 'cashed_out': return { label: 'COBRADA', color: 'text-blue-400', border: 'border-l-4 border-blue-500', headerBg: 'bg-[#2563eb]', badgeText: 'text-white' };
        case 'approved': return { label: 'APROBADA', color: 'text-emerald-400', border: 'border-l-4 border-emerald-500', headerBg: 'bg-[#059669]', badgeText: 'text-white' };
        case 'rejected': return { label: 'RECHAZADA', color: 'text-red-400', border: 'border-l-4 border-red-500', headerBg: 'bg-[#dc2626]', badgeText: 'text-white' };
        default: return { label: 'ANULADA', color: 'text-slate-400', border: 'border-l-4 border-slate-500', headerBg: 'bg-slate-600', badgeText: 'text-white' };
    }
};
/**
 * Envuelve una URL externa con nuestro proxy de backend para evitar bloqueos CSP/CORS.
 */
export const getProxyImage = (url) => {
    if (!url) return null;
    // Si la imagen ya es local o ya está proxied, no hacemos nada
    if (url.startsWith('/') || url.startsWith(window.location.origin)) return url;
    return `/api/img-proxy?url=${encodeURIComponent(url)}`;
};

export const getFlagUrl = (name) => {
    if (!name) return null;
    const n = name.toLowerCase();
    const flags = {
        'argentina': 'ar', 'brasil': 'br', 'brazil': 'br', 'bolivia': 'bo',
        'chile': 'cl', 'colombia': 'co', 'ecuador': 'ec', 'paraguay': 'py',
        'peru': 'pe', 'uruguay': 'uy', 'venezuela': 've', 'españa': 'es',
        'spain': 'es', 'mexico': 'mx', 'méxico': 'mx', 'usa': 'us',
        'estados unidos': 'us', 'inglaterra': 'gb-eng', 'england': 'gb-eng',
        'francia': 'fr', 'france': 'fr', 'alemania': 'de', 'germany': 'de',
        'italia': 'it', 'italy': 'it', 'portugal': 'pt', 'japón': 'jp',
        'japan': 'jp', 'china': 'cn', 'corea': 'kr', 'korea': 'kr',
        'bélgica': 'be', 'belgium': 'be', 'holanda': 'nl', 'netherlands': 'nl',
        'paises bajos': 'nl', 'países bajos': 'nl',
        'croacia': 'hr', 'croatia': 'hr', 'suiza': 'ch', 'switzerland': 'ch',
        'austria': 'at', 'grecia': 'gr', 'greece': 'gr', 'turquía': 'tr',
        'turkey': 'tr', 'rusia': 'ru', 'russia': 'ru', 'canadá': 'ca',
        'canada': 'ca', 'australia': 'au', 'egipto': 'eg', 'egypt': 'eg',
        'marruecos': 'ma', 'morocco': 'ma', 'senegal': 'sn', 'india': 'in',
        'indonesia': 'id', 'malaysia': 'my', 'malasia': 'my', 'tailandia': 'th',
        'thailand': 'th', 'vietnam': 'vn', 'filipinas': 'ph', 'philippines': 'ph',
        'arabia saudita': 'sa', 'saudi arabia': 'sa', 'emiratos': 'ae', 'uae': 'ae',
        'champions league': 'eu', 'europa league': 'eu', 'euro': 'eu',
        'serbie': 'rs', 'serbia': 'rs', 'escocia': 'gb-sct', 'scotland': 'gb-sct',
        'irlanda': 'ie', 'ireland': 'ie', 'gales': 'gb-wls', 'wales': 'gb-wls',
        'suecia': 'se', 'sweden': 'se', 'dinamarca': 'dk', 'denmark': 'dk',
        'polonia': 'pl', 'poland': 'pl', 'nigeria': 'ng', 'camerun': 'cm',
        'camerún': 'cm', 'ghana': 'gh', 'costa de marfil': 'ci',
        'pakistan': 'pk', 'pakistán': 'pk', 'bangladesh': 'bd',
        'west indies': 'jm', 'sri lanka': 'lk', 'south africa': 'za', 'sudáfrica': 'za',
        'new zealand': 'nz', 'nueva zelanda': 'nz',
        'curaçao': 'cw', 'curazao': 'cw', 'túnez': 'tn', 'tunez': 'tn',
        'irán': 'ir', 'iran': 'ir', 'cape verde': 'cv', 'cabo verde': 'cv',
        'iraq': 'iq', 'irak': 'iq', 'noruega': 'no', 'norway': 'no',
        'jordan': 'jo', 'jordania': 'jo', 'argelia': 'dz', 'algeria': 'dz',
        'panama': 'pa', 'panamá': 'pa', 'dr congo': 'cd', 'congo': 'cd',
        'bosnia & herzegovina': 'ba', 'bosnia': 'ba', 'catar': 'qa', 'qatar': 'qa',
        'haiti': 'ht', 'haití': 'ht', 'republica checa': 'cz', 'república checa': 'cz',
        'czech republic': 'cz', 'uzbekistan': 'uz', 'uzbekistán': 'uz'
    };
    for (const [key, code] of Object.entries(flags)) {
        if (n.includes(key)) {
            // No usamos proxy para flagcdn ya que soporta CORS y es público
            return `https://flagcdn.com/w80/${code}.png`;
        }
    }
    return null;
};
